"use client";

import { useTranslation } from "@/context/TranslationContext";
import { showToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";
import { useState, useEffect, useRef } from "react";

export const useOTPInput = (
  isOpen: boolean,
  onVerify: (code: string) => void
) => {
  const { t } = useTranslation();
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const otpInputs = useRef<Array<HTMLInputElement | null>>([]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const fullCode = code.join("");
    if (fullCode.length === 6) {
      onVerify(fullCode);
    } else {
      showToast(TOAST_TYPES.error, t("Enter the 6-digit code"));
    }
  };

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    // Move to the previous input field if Backspace is pressed in an empty input
    if (event.key === "Backspace") {
      const newCode = [...code];
      newCode[index] = "";
      setCode(newCode);
      if (index > 0) {
        otpInputs.current[index - 1]?.focus();
      } else {
        otpInputs.current[0]?.focus();
      }
      return;
    }
    if (/\d/.test(event.key)) {
      const newCode = [...code];
      newCode[index] = event.key;
      setCode(newCode);

      if (event.key && index < 5) {
        otpInputs.current[index + 1]?.focus();
      } else if (!event.key && index > 0) {
        otpInputs.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number
  ) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();
    if (pastedData.length > 6) return; // Prevent pasting more than 6 characters

    const newCode = [...code];

    if (pastedData.length > 1) {
      // If pasting multiple characters, fill from the first index
      for (let i = 0; i < pastedData.length && i < newCode.length; i++) {
        newCode[i] = pastedData[i];
      }
    } else if (pastedData.length === 1) {
      // If pasting single character, fill only in the current field
      newCode[index] = pastedData[0];
    }

    setCode(newCode);
  };

  useEffect(() => {
    if (isOpen) {
      // Focus first input when modal opens
      setTimeout(() => {
        const firstInput = document.getElementById("otp-0");
        firstInput?.focus();
      }, 100);
    } else {
      setCode(["", "", "", "", "", ""]);
    }
  }, [isOpen]);

  return {
    code,
    otpInputs,
    handleKeyDown,
    handlePaste,
    handleSubmit,
    formatCountdown,
  };
};
